/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Component, Inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { EventMessage, Events, EventService } from '@app/common/event';
import { UserPrivilegesService } from '@app/common/user-privileges.service';
import { TranslateService } from '@ngx-translate/core';
import { NotifyService } from '@app/common/notify.service';
import { Logger } from '@core/lib/services';
import { AppConfigService } from '@app/common/app-config.service';
import { EventWorkItem } from '@app/data-model/event-work-item';
import { AuditService } from '@app/common/audit.service';
import { UtilService } from '@app/util/util.service';
import { DOCUMENT } from '@angular/common';
import { NativeAcademicWorkflowService } from '@app/di/native-academic-workflow.service';
import { filter, finalize, first, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';

const log = new Logger('WorkItemStateComponent');

@Component({
  selector: 'app-work-item-state',
  templateUrl: './work-item-state.component.html'
})
export class WorkItemStateComponent implements OnChanges {
  @Input() id;
  @Input() item;
  @Input() state;
  @Input() userId;

  loading = true;
  isMouseOver = false;
  isRemoteExam = false;
  isGlobalExam = false;

  private window: Window;
  private toggleAssignmentTimeout;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private translate: TranslateService,
    private notify: NotifyService,
    private appConfigService: AppConfigService,
    private eventService: EventService,
    private userPrivilegesService: UserPrivilegesService,
    private auditService: AuditService,
    private nativeAcademicWorkflowService: NativeAcademicWorkflowService
  ) {
    this.window = (this.document as Document).defaultView;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.state) {
      if (changes.state.currentValue) {
        this.loading = false;
      }
    }
    this.isRemoteExam = UtilService.isRemoteExam(this.item);

    this.isGlobalExam = UtilService.isGlobalExam(this.item);
  }

  toggleAssignmentIfAllowedEvent(event) {
    event.stopPropagation();

    // timeout required to eliminate tooltip issue.
    // the code is taking enough time to cause the
    // tooltip directive not detect the mouseleave event
    this.toggleAssignmentTimeout = this.window.setTimeout(() => this.toggleAssignmentIfAllowed(), 100);
  }

  toggleAssignmentIfAllowed() {
    let auditData = {} as any;
    const data = { id: this.id };
    if (this.item) {
      auditData = this.auditService.getExamAuditInfo(this.item);
    }
    if (this.canToggleAssignment()) {
      if (!this.state.isAssigned) {
        this.loading = true;
        const actor = this.appConfigService.getUserId();
        auditData.ownerName = actor;

        this.eventService
          .sendEvent(
            new EventMessage(Events.assign, new EventWorkItem(this.id, data), {
              userName: actor,
              examAudit: auditData
            })
          )
          .then(
            () => log.info(`Assign exam success, ${this.id}, ${this.item.accession}`),
            () => this.notify.error(this.translate.instant('workItem.assignText'))
          )
          .finally(() => (this.loading = false));
      } else if (this.state.isAssignedToSelf && !this.state.isActivatedBySelf) {
        this.loading = true;

        (this.nativeAcademicWorkflowService.showNAWReassignWarningDialog({ state: this.state }) as Observable<boolean>)
          .pipe(
            first(),
            filter(Boolean),
            tap(_ =>
              this.eventService
                .sendEvent(
                  new EventMessage(Events.unassign, new EventWorkItem(this.id, data), { examAudit: auditData })
                )
                .then(
                  () => {
                    log.info(`Unassign exam success, ${this.id}, ${this.item.accession}`);
                  },
                  () => this.notify.error(this.translate.instant('workItem.unassignText'))
                )
            ),
            finalize(() => {
              this.loading = false;
              this.window.setTimeout(() => {
                const elms = this.document.getElementById(this.id + '-status-indicator-tooltip');
                if (elms && elms.parentNode && elms.parentNode.parentNode) {
                  elms.parentNode.parentNode.setAttribute('style', 'display: none');
                }
              }, 3000);
            })
          )
          .subscribe(
            _ => log.info(`Unassign process finished, ${this.id}, ${this.item.accession}`),
            _ => log.info(`Error during unassign, ${this.id}, ${this.item.accession}`)
          );
      }
    }
    this.window.clearTimeout(this.toggleAssignmentTimeout);
  }

  private canToggleAssignment() {
    return (
      !this.state.isActivated && !this.state.isAssignedToOther && this.canSelfAssign() && !this.state.activity?.editing
    );
  }

  private canSelfAssign() {
    return (
      this.userPrivilegesService.hasPrivilege('Dictate') || this.userPrivilegesService.hasPrivilege('ID_Assignment')
    );
  }
}
